import React, { Component } from 'react';
import _, { isString } from 'lodash';
import validateField, { areFieldsLoadedAndValid } from './validate';
import FormFields from './FormFields';
import { formDefaultProps, formInitialState, formPropTypes } from './propTypes';
import { FIELD_RULE_TYPES } from '../constants';

class Form extends Component {
  state = formInitialState;
  static defaultProps = formDefaultProps;

  componentDidMount = () => {
    this.addFormFields(this.props.form);
  };

  componentDidUpdate = (prevProps) => {
    const { form: prevForm } = prevProps;
    const { form: newForm } = this.props;

    if (prevForm !== newForm) {
      this.addFormFields(newForm);
    }
  };

  addFormFields = (form) => {
    const {
      state: { fields: prevFields, cachedValues },
      props: { initialValues: propsInitialValues },
    } = this;
    const { fields: newFields, number: formNumber } = form;

    // Preparing cached values
    const newCachedValues = { ...cachedValues };

    _.forEach(prevFields, ({ name, value, isCaching, disabled }) => {
      if (!isCaching || disabled) {
        return;
      }

      newCachedValues[name] = value;
    });

    // Preparing fields
    const newFieldsDependencies = {};

    const preparedFields = _.chain(newFields)
      .map((initialField) => {
        const { name, value: initialValue, rules, number: fieldNumber } = initialField;
        let value = _.find(
          [newCachedValues[name], initialValue, propsInitialValues[name]],
          (val) => val || _.isFinite(val)
        );

        if (_.isUndefined(value)) {
          value = null;
        }

        _.forEach(rules, ({ type, ruleOption }) => {
          const ref = type === FIELD_RULE_TYPES.EQUAL && ruleOption;

          if (!ref) {
            return;
          }

          if (newFieldsDependencies[ref]) {
            newFieldsDependencies[ref].push(name);
          } else {
            newFieldsDependencies[ref] = [name];
          }
        });

        return {
          key: `${formNumber}-${fieldNumber}-${name}`,
          active: false,
          touched: false,
          isLoading: false,
          warnings: [],
          ...initialField,
          value,
          errors: [],
          dependencies: [],
        };
      })
      .keyBy('name')
      .value();

    // Collecting dependencies
    _.forEach(newFieldsDependencies, (newDependencies, name) => {
      const field = preparedFields[name];

      if (!field) {
        return;
      }

      const { dependencies: prevDependencies } = field;

      preparedFields[name].dependencies = _.union(prevDependencies, newDependencies);
    });

    // Validating preparedFields
    const loadingFields = [];
    _.forEach(preparedFields, (field) => {
      const { shouldValidateAsync, errors } = validateField(field, preparedFields);

      field.errors = errors;

      if (shouldValidateAsync) {
        field.isLoading = true;
        loadingFields.push(field);
      }
    });

    this.setState(
      {
        cachedValues: newCachedValues,
        fields: preparedFields,
        isLoading: false,
      },
      () => {
        this.validateFieldsAsync(loadingFields);
      }
    );
  };

  validateFieldsAsync = (fields) => {
    if (!fields.length) {
      return;
    }

    _.forEach(fields, (field) => {
      const { name, value } = field;
      const stateField = this.state.fields[name];
      const { value: stateFieldValue } = stateField;

      if (value !== stateFieldValue) {
        return;
      }

      this.validateFieldAsync(stateField);
    });
  };

  validateFieldAsync = (field) => {
    const { onValidateFieldAsync } = this.props;

    // Here I assume that request error was handled already
    onValidateFieldAsync(field, this.state.fields).then((errors) => {
      const { name, value } = field;
      const { fields } = this.state;
      const stateField = fields[name];
      const { value: stateFieldValue } = stateField;

      if (value !== stateFieldValue) {
        return;
      }

      this.setState({
        fields: {
          ...fields,
          [name]: {
            ...stateField,
            isLoading: false,
            errors,
          },
        },
      });
    });
  };

  focusFieldHandler = (name) => {
    const { fields } = this.state;
    const field = fields[name];

    this.setState({
      fields: {
        ...fields,
        [name]: {
          ...field,
          active: true,
        },
      },
    });
  };

  blurFieldHandler = (name) => {
    const { fields } = this.state;
    const field = fields[name];

    this.setState({
      fields: {
        ...fields,
        [name]: {
          ...field,
          active: false,
          touched: true,
        },
      },
    });
  };

  setGlobalErrorsHandler = (errors) => {
    this.setState({ globalErrors: errors });
  };

  setIsLoadingHandler = (isLoading) => {
    this.setState({ isLoading });
  };

  setFieldDisabled = (name, disabled) => {
    const { fields } = this.state;
    const field = fields[name];

    this.setState({
      fields: {
        ...fields,
        [name]: {
          ...field,
          disabled,
        },
      },
    });
  };

  enableFieldHandler = (name) => {
    this.setFieldDisabled(name, true);
  };

  disableFieldHandler = (name) => {
    this.setFieldDisabled(name, false);
  };

  setFieldErrorsHandler = (name, errors) => {
    const { fields } = this.state;
    const field = fields[name];

    this.setState({
      fields: {
        ...fields,
        [name]: {
          ...field,
          errors,
        },
      },
    });
  };

  setFieldValueHandler = (name, value) => {
    const { fields: prevFields } = this.state;
    const prevField = prevFields[name];
    const newField = {
      ...prevField,
      value,
    };

    const { applyTo, action } = newField; // apply some action to other field
    let newApplyToField;

    if (applyTo && _.isFunction(action)) {
      const applyToField = prevFields[applyTo];
      if (applyToField) {
        newApplyToField = {
          ...applyToField,
          value: action(applyToField.value, value),
        };
      }
    }

    const newFields = {
      ...prevFields,
      [name]: newField,
      ...(newApplyToField ? { [newApplyToField.name]: newApplyToField } : {}),
    };

    const loadingFields = [];
    const { shouldValidateAsync, errors } = validateField(newField, newFields);

    newField.errors = errors;

    if (shouldValidateAsync) {
      loadingFields.push(newField);
    }

    _.forEach(newField.dependencies, (dependencyName) => {
      const dependency = newFields[dependencyName];
      const { shouldValidateAsync: dependencyShouldValidateAsync, errors: dependencyErrors } =
        validateField(dependency, newFields);

      const updatedDependency = {
        ...dependency,
        errors: dependencyErrors,
      };
      newFields[dependencyName] = updatedDependency;

      if (dependencyShouldValidateAsync) {
        loadingFields.push(updatedDependency);
      }
    });

    this.setState(
      {
        globalErrors: [],
        fields: newFields,
      },
      () => {
        this.validateFieldsAsync(loadingFields);
      }
    );
  };

  submitHandler = async () => {
    const {
      setFieldValueHandler,
      disableFieldHandler,
      enableFieldHandler,
      setGlobalErrorsHandler,
      setIsLoadingHandler,
      setFieldErrorsHandler,
      props: { action, additionalValues, onSubmit, onError },
      state: { fields, globalErrors, cachedValues, isLoading },
    } = this;

    const isFormReady = areFieldsLoadedAndValid(fields) && _.isEmpty(globalErrors) && !isLoading;

    if (!isFormReady) {
      return;
    }

    try {
      const result = await action(
        {
          ...cachedValues,
          ...additionalValues,
          ..._.reduce(
            fields,
            (accumulator, field) => {
              const [name, value, disabled] = _.at(field, ['name', 'value', 'disabled']);

              if (disabled) {
                return accumulator;
              }

              return {
                ...accumulator,
                [name]: isString(value) ? value.trim() : value,
              };
            },
            {}
          ),
        },
        {
          setFieldValue: setFieldValueHandler,
          disableField: disableFieldHandler,
          enableField: enableFieldHandler,
          setGlobalErrors: setGlobalErrorsHandler,
          setIsLoading: setIsLoadingHandler,
          setFieldErrors: setFieldErrorsHandler,
        }
      );

      onSubmit(result);
    } catch (error) {
      onError(error);
    }
  };

  render() {
    return (
      <FormFields
        fieldComponents={this.props.fieldComponents}
        ContainerComponent={this.props.ContainerComponent}
        onFocusField={this.focusFieldHandler}
        onBlurField={this.blurFieldHandler}
        onDisableField={this.disableFieldHandler}
        onEnableField={this.enableFieldHandler}
        onSetFieldErrors={this.setFieldErrorsHandler}
        onSetFieldValue={this.setFieldValueHandler}
        onSetGlobalErrors={this.setGlobalErrorsHandler}
        onSetIsLoading={this.setIsLoadingHandler}
        onSubmit={this.submitHandler}
        formNumber={this.props.form.number}
        fields={this.state.fields}
        globalErrors={this.state.globalErrors}
        isLoading={this.state.isLoading}
      />
    );
  }
}

Form.propTypes = formPropTypes

export default Form
